import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchItems, deleteItem, searchItems, bulkDeleteItems, bulkUpdateItemStatus } from "./itemSlice";
import Swal from 'sweetalert2';
import ConfirmationModal from "../../components/ConfirmationModal";
import Loader from "../../components/Loader";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import log from "../../logger";
import ReactTable from "../../components/ReactTable";
import { paths } from "../../routes/helpers";
import { getImageUrl } from "../../utils/urlUtils";
import useDebounce from "../../hooks/useDebounce";

const ItemList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error, total, pages } = useSelector(
    (state) => state.item
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  useEffect(() => {
    if (debouncedSearchTerm) {
      dispatch(searchItems({ keyword: debouncedSearchTerm, page: 1, limit: currentLimit }));
    } else {
      dispatch(fetchItems({ page: currentPage, limit: currentLimit }));
    }
  }, [dispatch, currentPage, currentLimit, debouncedSearchTerm]);

  // Reset current page when search term changes
  useEffect(() => {
    if (searchTerm) {
      setCurrentPage(1);
    }
  }, [searchTerm]);

  const handleDelete = (itemId) => {
    setShowDeleteModal(true);
    setSelectedItem(itemId);
  };

  const confirmDelete = () => {
    if (selectedItem) {
      dispatch(deleteItem(selectedItem));
      toast.success("Item deleted successfully!");
      log.info("Item deleted:", selectedItem);
      setShowDeleteModal(false);
      setSelectedItem(null);
    }
  };

  const handleEdit = (itemId) => {
    navigate(paths.private.item.edit(itemId));
  };

  const handleCreateItem = () => {
    navigate(paths.private.item.create());
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setCurrentLimit(newLimit);
    setCurrentPage(1);
    if (debouncedSearchTerm) {
      dispatch(searchItems({ keyword: debouncedSearchTerm, page: 1, limit: newLimit }));
    } else {
      dispatch(fetchItems({ page: 1, limit: newLimit }));
    }
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleBulkDelete = async (ids, clearSelection) => {
    const result = await Swal.fire({
      title: `Delete ${ids.length} item${ids.length > 1 ? 's' : ''}?`,
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    dispatch(bulkDeleteItems(ids)).then(() => {
      Swal.fire({ title: 'Deleted!', text: `${ids.length} item${ids.length > 1 ? 's' : ''} deleted.`, icon: 'success', timer: 1500, showConfirmButton: false });
      clearSelection();
    });
  };

  const handleBulkStatusChange = (status) => async (ids, clearSelection) => {
    const activating = status === 1;
    const result = await Swal.fire({
      title: `${activating ? 'Activate' : 'Deactivate'} ${ids.length} item${ids.length > 1 ? 's' : ''}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, ${activating ? 'activate' : 'deactivate'}`,
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    dispatch(bulkUpdateItemStatus({ ids, status })).then(() => {
      Swal.fire({ title: 'Updated!', text: `${ids.length} item${ids.length > 1 ? 's' : ''} ${activating ? 'activated' : 'deactivated'}.`, icon: 'success', timer: 1500, showConfirmButton: false });
      clearSelection();
    });
  };

  const formatTableData = (items) => {
    return items.map((item) => ({
      _id: item._id,
      title: item.title || '-',
      description: item.description || '-',
      category: item.category || '-',
      minPrice: item.minPrice || 0,
      maxPrice: item.maxPrice || 0,
      images: item.images.length > 0 ? item.images.map((imageUrl)=>getImageUrl(imageUrl)) : 'No Image',
      rating: item.rating || 0,
      status: item.status ? 'Active' : 'Inactive',
      createdAt: item.createdAt ? new Date(item.createdAt).toLocaleString() : '-',
      updatedAt: item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '-',
    }));
  };

  const headers = [
    { label: 'Image', key: 'images' },
    { label: 'Title', key: 'title' },
    { label: 'Description', key: 'description' },
    { label: 'Category', key: 'category' },
    { label: 'Min Price', key: 'minPrice' },
    { label: 'Max Price', key: 'maxPrice' },
    { label: 'Rating', key: 'rating' },
    { label: 'Status', key: 'status' },
    { label: 'Created At', key: 'createdAt' },
    { label: 'Updated At', key: 'updatedAt' },
  ];

  const actions = [
    { label: 'Edit', onClick: handleEdit, color: 'blue' },
    { label: 'Delete', onClick: handleDelete, color: 'red' },
  ];

  if (error) return (
    <div className="p-4">
      <div className="text-red-500">Error: {error}</div>
    </div>
  );

  return (
    <div className="p-4">
      <h1 className="text-xl font-bold mb-4">Items</h1>
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={handleCreateItem}
          className="bg-green-500 text-white px-4 py-2 rounded"
        >
          Create New Item
        </button>
        <div className="text-sm text-gray-600">
          {searchTerm ? `Found ${total} items` : `Total: ${total} items`}
        </div>
      </div>

      <ReactTable
        headers={headers}
        data={formatTableData(items)}
        actions={actions}
        onSearch={handleSearch}
        searchDelay={1000}
        searchValue={searchTerm}
        loading={loading}
        pagination={items.length > 0 ? { page: currentPage, limit: currentLimit, total, pages } : null}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        enableRowSelection={true}
        bulkActions={[
          { label: 'Activate Selected', color: 'green', onClick: handleBulkStatusChange(1) },
          { label: 'Deactivate Selected', color: 'gray', onClick: handleBulkStatusChange(0) },
          { label: 'Delete Selected', color: 'red', onClick: handleBulkDelete },
        ]}
      />

      {/* No results message */}
      {items.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg mt-4">
          <p className="text-gray-500">
            {searchTerm ? `No items found for "${searchTerm}"` : 'No items found'}
          </p>
          <p className="text-gray-400 text-sm mt-2">
            {searchTerm ? 'Try a different search term' : 'Click the Create New button to add one'}
          </p>
        </div>
      )}

      {showDeleteModal && (
        <ConfirmationModal
          message="Are you sure you want to delete this item?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default ItemList; 